import { useState } from 'react';

import { formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Qualification } from 'components/types';

import { getMaxDate } from 'utils/date';

import { EmployeeQualification } from 'RestAPIs/employees/qualifications';

import { Alert, Button, DialogActions, TextField } from '@mui/material';

import { DatePicker, Dialog, VirtualizedAutocomplete } from 'components/shared';
import { optionPadding } from 'components/shared/Autocomplete/MuiAutocompleteOption';
import {
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';

type SaveParams = {
  startDate: string;
  endDate: string | null;
  qualificationId: string;
};

export type Props = {
  qualifications: Qualification[];
  qualificationToEdit?: EmployeeQualification | null;
  onSave: (props: SaveParams) => void;
  onDialogHidden: () => void;
  dialogContainer?: HTMLElement;
};

export default function QualificationDialog({
  qualifications,
  qualificationToEdit,
  dialogContainer,
  onSave,
  onDialogHidden
}: Props) {
  const { t } = useTranslation('common');
  const isNew = !qualificationToEdit;
  const [showDialog, setShowDialog] = useState(true);
  const [selectedQualification, setSelectedQualification] =
    useState<Qualification | null>(
      qualificationToEdit
        ? qualifications.filter(
            ({ id }) => id === qualificationToEdit.qualificationId
          )[0]
        : null
    );
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(qualificationToEdit?.startDate || new Date())
  );
  const [endDate, setEndDate] = useState<Date | null>(
    new Date(qualificationToEdit?.endDate || getMaxDate())
  );

  const saveQualification = () => {
    if (selectedQualification && startDate) {
      onSave({
        startDate: formatISO(startDate, { representation: 'date' }),
        endDate:
          endDate && endDate < getMaxDate()
            ? formatISO(endDate, { representation: 'date' })
            : null,
        qualificationId: selectedQualification.id
      });
      setShowDialog(false);
    }
  };

  return (
    <Dialog
      open={showDialog}
      title={t(isNew ? 'addQualification' : 'editQualification')}
      maxWidth="extralarge"
      onHidden={onDialogHidden}
      container={dialogContainer || null}
    >
      <VirtualizedAutocomplete
        value={selectedQualification}
        options={qualifications}
        renderInput={(params) => (
          <TextField {...params} label={t('selectQualification')} />
        )}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.name;
        }}
        onChange={(e, value) =>
          setSelectedQualification(value as Qualification | null)
        }
        autoHighlight
        extraWidth={optionPadding * 2}
        disabled={!isNew}
      />

      <br />

      <GridContainer>
        <GridCompact6>
          <h4>{t('validFrom')}</h4>
          <DatePicker
            date={startDate}
            onChange={(date) => setStartDate(date)}
          />
        </GridCompact6>
        <GridCompact6>
          <h4>{t('validTo')}</h4>
          <DatePicker date={endDate} onChange={(date) => setEndDate(date)} />
        </GridCompact6>
      </GridContainer>

      {startDate && endDate && startDate > endDate && (
        <Alert severity="warning">{t('invalidInterval')}</Alert>
      )}

      <DialogActions>
        <Button
          onClick={() => setShowDialog(false)}
          color="secondary"
          variant="contained"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={saveQualification}
          color="primary"
          variant="contained"
          disabled={
            !selectedQualification ||
            !startDate ||
            !endDate ||
            startDate > endDate
          }
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
